import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function ComplianceOverview() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, h, cfg] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/commission/reconciliation-health`, { withCredentials: true }),
          axios.get(`${API}/settings/commission-tds`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setHealth(h.data);
        setSettings(cfg.data.settings);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load compliance overview');
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runBackfill = async () => {
    try {
      const r = await axios.post(`${API}/commission/backfill`, null, {
        withCredentials: true,
        params: { days: 365, limit: 5000 },
      });
      alert(`Backfill complete: ${r.data?.stamped ?? 0} bookings stamped`);
      window.location.reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Backfill failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const cards = [
    {
      label: 'Markup income (MTD)',
      value: fmt(stats?.markup_income_mtd),
      source: 'aggregate_confirmed_economics',
    },
    {
      label: 'ACF retained commission (MTD)',
      value: fmt(stats?.acf_commission_retained_mtd),
      source: 'economics (stamped or fare_breakdown fallback)',
    },
    {
      label: 'Commission GST liability (MTD)',
      value: fmt(stats?.gst_on_commission_mtd),
      source: 'estimated 18% on ACF retained',
    },
    {
      label: 'Agent commission shared (MTD)',
      value: fmt(stats?.agent_commission_shared_mtd),
      source: 'commission_accounting when stamped',
    },
    {
      label: 'Supplier gross commission (MTD)',
      value: fmt(stats?.airline_commission_mtd),
      source: 'fare_breakdown.airline_commission',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Compliance Overview</h2>
        <p className="text-sm text-gray-500 mt-1">MTD tax & commission KPIs</p>
        <div className="flex flex-wrap gap-3 mt-2 text-xs">
          <Link to="/admin/compliance/commission-tds?period=mtd" className="text-[#1B375B] font-semibold underline">
            Commission & TDS Reports
          </Link>
          <Link to="/admin/compliance/gst-report" className="text-[#1B375B] font-semibold underline">GSTR-1</Link>
          <Link to="/admin/compliance/ca-exports" className="text-[#1B375B] font-semibold underline">CA Exports</Link>
          <Link to="/admin/compliance/credit-notes" className="text-[#1B375B] font-semibold underline">Credit Notes</Link>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
      )}

      <div className={`rounded-xl border p-4 text-sm ${settings?.commission_tds_enabled ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        Commission & TDS module: <strong>{settings?.commission_tds_enabled ? 'ON' : 'OFF'}</strong>
        {' · '}
        Auto-credit: <strong>{settings?.auto_credit_agent_commission_wallet ? 'ON' : 'OFF'}</strong>
        {' · '}
        <Link to="/admin/compliance/settings" className="text-[#1B375B] font-semibold underline">Settings</Link>
      </div>

      {health && (
        <div className={`rounded-xl border p-4 text-sm ${health.drift ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid="compliance-recon-table">
          <p className="font-semibold mb-3 flex items-center gap-2">
            {health.drift && <AlertTriangle className="w-4 h-4" />}
            Reconciliation (MTD) — {health.drift ? 'drift detected' : 'aligned'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="p-2">Layer</th>
                  <th className="p-2 text-right">ACF retained</th>
                  <th className="p-2 text-right">Agent shared</th>
                  <th className="p-2">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-200">
                  <td className="p-2 font-medium">Dashboard economics</td>
                  <td className="p-2 text-right">{fmt(health.dashboard_mtd?.acf_commission_retained)}</td>
                  <td className="p-2 text-right">{fmt(health.dashboard_mtd?.agent_commission_shared)}</td>
                  <td className="p-2 text-gray-500">{health.dashboard_mtd?.data_source}</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-2 font-medium">
                    Stamped ({health.commission_tds_mtd?.stamped_bookings ?? 0} bookings)
                  </td>
                  <td className="p-2 text-right">{fmt(health.commission_tds_mtd?.acf_retained)}</td>
                  <td className="p-2 text-right">{fmt(health.commission_tds_mtd?.agent_shared)}</td>
                  <td className="p-2 text-gray-500">{health.commission_tds_mtd?.data_source}</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-2 font-medium">Ledger posted</td>
                  <td className="p-2 text-right">{fmt(health.ledger_mtd?.supplier_commission_income)}</td>
                  <td className="p-2 text-right">—</td>
                  <td className="p-2 text-gray-500">{health.ledger_mtd?.data_source}</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-2 font-medium">
                    Invoice snapshot ({health.invoice_snapshot_mtd?.invoice_count ?? 0} invoices)
                  </td>
                  <td className="p-2 text-right">{fmt(health.invoice_snapshot_mtd?.acf_commission_retained)}</td>
                  <td className="p-2 text-right">—</td>
                  <td className="p-2 text-gray-500">{health.invoice_snapshot_mtd?.data_source}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {(health.unstamped_bookings_mtd > 0 || health.drift) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {health.unstamped_bookings_mtd > 0 && (
                <span>{health.unstamped_bookings_mtd} MTD bookings lack stamped commission blocks.</span>
              )}
              {settings?.commission_tds_enabled && (
                <button
                  type="button"
                  onClick={runBackfill}
                  className="px-2 py-1 bg-[#1B375B] text-white rounded font-semibold"
                >
                  Run backfill
                </button>
              )}
              <Link to="/admin/compliance/commission-tds" className="underline font-semibold text-[#1B375B]">
                View reports →
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{c.value}</p>
            <p className="text-[10px] text-gray-400 mt-1">Source: {c.source}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
